import { Box, Modal, ScrollArea, Text } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { getRelatedCxItem } from "../../../../../../Api";
import TicketCommentItem from "./TicketCommentItem";
import SupportTicketTextArea from "./SupportTicketTextArea";
import TicketTitle from "./TicketTitle";
import JumpToExcerpt from "./JumpToExcerpt";
import DynamicFieldRenderer from "./DynamicFieldRenderer";
import {useKnowledgeData} from "../../../../../../hooks/useKnowledgeData";
import { resolveEvidenceDisplayOrigin } from "../../../../../../utils/evidence/evidenceDisplayOrigin";
import { getOriginalItemViewLabel } from "../../../../../../utils/evidence/businessCategoryOriginalLabels";
import { getEvidenceDisplayTitle } from "../../../../../../utils";

const SupportTicket = ({ item, isLoadingMl = false }: { item: any; isLoadingMl?: boolean }) => {

  const { members } = useKnowledgeData();
  const auth0 = useAuth0();
  const [opened, setOpened] = useState(false);
  const [sortedTextItems, setSortedTextItems] = useState<any[]>([]);

  const text = item?.text;
  // A blank title falls back to the first few words of the description — the text area
  // below still shows the full, untruncated text unchanged.
  const title = getEvidenceDisplayTitle(item?.title, text) || undefined;

  const cxItem = item?.cxItem;
  const origin = resolveEvidenceDisplayOrigin(item) || "";
  const ticketLinkLabel =
    getOriginalItemViewLabel(cxItem?.businessCategory) ||
    getOriginalItemViewLabel("ticket");
  const originContactId = cxItem?.originContactId;
  const originOwnerId = cxItem?.originOwnerId;
  const originSubmitterId = cxItem?.originSubmitterId;
  const contactEmail = cxItem?.contactEmail;
  const ownerEmail = cxItem?.ownerEmail;
  const submitterEmail = cxItem?.submitterEmail;
  const contactName = cxItem?.contactName;
  const ownerName = cxItem?.ownerName;
  const submitterName = cxItem?.submitterName;
  const excerptsArr = item?.excerpts || [];

  // Check if we should show as conversation (no comments available)
  const [hasComments, setHasComments] = useState<boolean | null>(null);
  const [fullCxItem, setFullCxItem] = useState<any>(null);
  const conversationTitle = title;
  const conversationContent = text;

  const [refsIndexes, setRefsIndexes] = useState<any[]>([]);

  const refsArr = useRef<(HTMLDivElement | null)[]>([]);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const scrollToElement = (index: number) => {
    if (refsArr.current[index] && scrollAreaRef.current) {
      const element = refsArr.current[index];
      const scrollArea = scrollAreaRef.current;
      
      if (element) {
        // Get the element's position relative to the scroll area
        const elementRect = element.getBoundingClientRect();
        const scrollAreaRect = scrollArea.getBoundingClientRect();
        
        // Calculate the scroll position to center the element
        const elementTop = element.offsetTop;
        const scrollAreaHeight = scrollArea.clientHeight;
        const elementHeight = element.clientHeight;
        
        const scrollTop = elementTop - (scrollAreaHeight / 2) + (elementHeight / 2);
        
        scrollArea.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      }
    }
  };

  const scrollToTop = () => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const loadRelatedTextItems = (id: string) => {
    if (!id) {
      setHasComments(false);
      return;
    }
    getRelatedCxItem(id, auth0)
      .then((res) => {
        // Store the full cxItem with _mapping_meta
        setFullCxItem(res);
        
        const items = res?.comments || {};
        const itemsArray: any[] = Object.values(items);
        
        if (itemsArray?.length === 0) {
          setHasComments(false);
          loadConversationContent();
          return;
        }

        setHasComments(true);

        excerptsArr.forEach((excerptItem: any) => {
          const { excerptCommentId } = excerptItem;

          if (items.hasOwnProperty(excerptCommentId)) {
            items[excerptCommentId].isExcerpt = true;
            items[excerptCommentId].commentId = excerptCommentId;
          }
        });
        
        const sortedItems: any[] =
          itemsArray?.sort(
            (a: any, b: any) =>
              new Date(a.createdAt).getTime() -
              new Date(b.createdAt).getTime()
          ) || [];

        sortedItems.forEach((commentItem: any, index: number) => {
          if (commentItem?.isExcerpt) {

            setRefsIndexes((prevIndexes) => [...prevIndexes, index]);
          }

          if (commentItem?.originSubmitterId && !commentItem?.fromEmail) {
            const orginSub = commentItem?.originSubmitterId;

            if (orginSub === originContactId) {
              commentItem.fromEmail = contactEmail;
            } else if (orginSub === originOwnerId) {
              commentItem.fromEmail = ownerEmail;
            } else if (orginSub === originSubmitterId) {
              commentItem.fromEmail = submitterEmail;
            }
          }

          if (commentItem?.originSubmitterId && !commentItem?.fromName) {
            const orginSub = commentItem?.originSubmitterId;

            if (orginSub === originContactId) {
              commentItem.fromName = contactName;
            } else if (orginSub === originOwnerId) {
              commentItem.fromName = ownerName;
            } else if (orginSub === originSubmitterId) {
              commentItem.fromName = submitterName;
            }
          }
        });

        sortedItems.forEach((commentItem: any) => {
          if (commentItem?.fromEmail) {
            const memberFromEmail = members.find(
              (m: any) => m.email === commentItem?.fromEmail
            );
            if (memberFromEmail) {
              commentItem.fromUser = memberFromEmail;
            }
          }
        });

        const firstExcerptIndex = sortedItems.findIndex(
          (item) => item.isExcerpt
        );
        const lastExcerptIndex = sortedItems
        
          .slice()
          .reverse()
          .findIndex((item) => item.isExcerpt);

        if (firstExcerptIndex !== -1) {
          sortedItems[firstExcerptIndex].isFirstExcerpt = true;
        }

        if (lastExcerptIndex !== -1) {
          sortedItems[
            sortedItems.length - 1 - lastExcerptIndex
          ].isLastExcerpt = true;
        }

        setSortedTextItems(sortedItems);
      })
      .catch((e) => {
        console.log(e);
        setHasComments(false);
        loadConversationContent();
      });
  };

  const loadConversationContent = () => {
    if (!cxItem) {
      setSortedTextItems([]);
      return;
    }

    // Create a single conversation item that will use DynamicFieldRenderer
    const conversationItem = {
      isConversation: true,
      isExcerpt: excerptsArr.length > 0,
      isFirstExcerpt: excerptsArr.length > 0,
      isLastExcerpt: excerptsArr.length > 0,
      createdAt: cxItem?.updatedAt || cxItem?.createdAt,
    };

    // Set refs index for the conversation content if there are excerpts
    if (excerptsArr.length > 0) {
      setRefsIndexes([0]);
    }
    setSortedTextItems([conversationItem]);
  };

  useEffect(() => {
    setRefsIndexes([]);
    setHasComments(null);

    if (item?.cxItemId) {
      loadRelatedTextItems(item?.cxItemId);
    } else {
      setHasComments(false);
      loadConversationContent();
    }
  }, [item]);

  return (
    <Box mt={15}>
      {opened && <Modal
        opened={opened}
        centered
        onClose={() => setOpened(false)}
        size="800px"
        zIndex={1100}
        title={<TicketTitle item={item} cxItem={cxItem} origin={origin} hasComments={hasComments} />}
      >
      
        <Box w={578}>
          <ScrollArea
            h={500}
            mah={500}
            w={750}
            maw={750}
            offsetScrollbars
            type="always"
            viewportRef={scrollAreaRef}
            pl={20}
          >
            { excerptsArr?.length > 0 &&
              <Text
                color="#5C5CEB"
                px="sm"
                my="md"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  const firstExcerptIndex = refsIndexes[0];
                  scrollToElement(firstExcerptIndex);
                }}
                underline
              >
                  Jump to first excerpt
              </Text>
            }
            {sortedTextItems && sortedTextItems.length > 0 ? (
              sortedTextItems.map((sortedItem: any, index: number) => (
                <Box
                  key={index}
                  ref={
                    sortedItem?.isExcerpt
                      ? (el) => {
                        refsArr.current[index] = el;
                      }
                      : null
                  }
                  bg={sortedItem.isExcerpt ? "#FFFDD4" : ""}
                >
                  {hasComments === false ? (
                    <DynamicFieldRenderer
                      cxItem={fullCxItem || cxItem}
                      excerpts={excerptsArr}
                      fallbackTitle={title}
                      fallbackText={text}
                      itemRef={
                        sortedItem?.isExcerpt
                          ? (el) => {
                            refsArr.current[index] = el;
                          }
                          : undefined
                      }
                    />
                  ) : (
                    <TicketCommentItem
                      sortedItem={sortedItem}
                      excerpts={excerptsArr}
                    />
                  )}

                  {!sortedItem.isLastExcerpt && sortedItem.isExcerpt && (
                    <JumpToExcerpt onClick={() => {
                      const nextExcerptIndex = Array.from(refsIndexes).find(
                        (refIndex) => refIndex > index
                      );
                      scrollToElement(nextExcerptIndex);
                    }} isLast={false}/>
                  )}
                  {sortedItem.isLastExcerpt && (
                    <JumpToExcerpt onClick={scrollToTop} isLast/>
                  )}
                </Box>
              ))
            ) : (
              <Text ml={80}>
                {hasComments === false ? "Content not available" : "Support ticket not available"}
              </Text>
            )}
          </ScrollArea>
        </Box>
    
      </Modal>}

      <SupportTicketTextArea
        title={hasComments === false ? conversationTitle : title}
        text={hasComments === false ? conversationContent : text}
        mlData={item?.ml}
        excerptsArr={excerptsArr}
        setOpened={setOpened}
        isLoadingMl={isLoadingMl}
        originalItemLinkLabel={ticketLinkLabel}
      />
    </Box>
  );
};

export default SupportTicket;
